import { useQueryClient } from '@tanstack/react-query'
import { usePutServicetypesReorder, getGetServicetypesQueryKey } from '@/api/generated/service-type/service-type'
import { getViewServiceErrorMessage } from '@/pages/view-service/hooks/getViewServiceErrorMessage'
import { getServiceApiErrorBody } from '@/hooks/getServiceApiErrorBody'

/**
 * Persists a new service-type ordering. Replaces the `changeServiceOrder` Redux thunk:
 * the React Query mutation invalidates the shared service-types query so every consumer
 * (see useServiceTypes) reflects the new order.
 *
 * The reorder endpoint takes a JSON *array* body, which needs frontend-utils >= 1.0.162
 * (earlier releases only JSON-stringified plain objects, so array bodies threw
 * "Expected BodyInit, got object" and the PUT never left the browser).
 *
 * On failure the caller's `onError` receives a mapped, user-facing message so the drag
 * can be rolled back and a toast shown — a failed PUT must not silently leave the UI
 * showing an order the server never accepted.
 */
export const useReorderServiceTypes = () => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = usePutServicetypesReorder()

  const reorder = (orderedServiceTypeIds: string[], opts?: { onError?: (message: string) => void }) => {
    mutate(
      { data: orderedServiceTypeIds },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetServicetypesQueryKey() })
        },
        onError: (error) => {
          opts?.onError?.(getViewServiceErrorMessage(getServiceApiErrorBody(error)))
        },
      },
    )
  }

  return { reorder, isPending }
}
